import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Space,
  Typography,
  Statistic,
  Row,
  Col,
  Input,
  Alert,
  Divider,
  message,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../api/axiosConfig";

const { Title, Text } = Typography;

const SalesLocationVerification = () => {
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [updatingProvincias, setUpdatingProvincias] = useState(false);
  const [stats, setStats] = useState(null);
  const [matches, setMatches] = useState({ data: [], total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);

  // Cargar estadísticas iniciales
  useEffect(() => {
    fetchStats();
    fetchMatches(1);
  }, []);

  // Obtener estadísticas
  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get(
        "/sales-location-verification/stats"
      );
      setStats(response.data[0]); // Obtener las estadísticas más recientes
    } catch (error) {
      console.error("Error al obtener estadísticas:", error);
      setError("Error al cargar las estadísticas");
    }
  };

  // Obtener coincidencias
  const fetchMatches = async (page, search = "") => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        "/sales-location-verification/matches",
        {
          params: { page, limit: 10, search },
        }
      );
      setMatches(response.data);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error al obtener coincidencias:", error);
      setError("Error al cargar las coincidencias");
    } finally {
      setLoading(false);
    }
  };

  // Iniciar verificación
  const startVerification = async () => {
    setVerifying(true);
    setError(null);
    try {
      await axiosInstance.post("/sales-location-verification/start");
      await fetchStats();
      await fetchMatches(1);
      setError(null);
    } catch (error) {
      console.error("Error en la verificación:", error);
      setError(error.response?.data?.error || "Error durante la verificación");
    } finally {
      setVerifying(false);
    }
  };

  // Actualizar provincias
  const updateProvincias = async () => {
    setUpdatingProvincias(true);
    setError(null);
    try {
      const response = await axiosInstance.post(
        "/sales-location-verification/update-provincias"
      );
      await fetchMatches(currentPage, searchTerm);
      setError(null);

      // Mostrar mensaje de éxito con más detalles
      const {
        registrosActualizados,
        totalRegistros,
        registrosSinCambios,
        registrosSinCoincidencias,
      } = response.data;
      message.success(
        `Actualización de provincias completada: 
        ${registrosActualizados} actualizados,
        ${registrosSinCambios || 0} sin cambios,
        ${registrosSinCoincidencias || 0} sin coincidencias.
        De un total de ${totalRegistros} registros revisados.`
      );

      return response.data;
    } catch (error) {
      console.error("Error al actualizar provincias:", error);
      setError(error.response?.data?.error || "Error al actualizar provincias");
      message.error("Error al actualizar provincias");
    } finally {
      setUpdatingProvincias(false);
    }
  };

  // Manejar búsqueda
  const handleSearch = () => {
    setCurrentPage(1);
    fetchMatches(1, searchTerm);
  };

  // Columnas de la tabla
  const columns = [
    {
      title: "Chasis",
      dataIndex: "chasis",
      key: "chasis",
    },
    {
      title: "Agencia",
      dataIndex: "agencia_code",
      key: "agencia_code",
    },
    {
      title: "Bodega",
      dataIndex: "des_bodega",
      key: "des_bodega",
    },
    {
      title: "Fecha Venta",
      dataIndex: "fecha_venta",
      key: "fecha_venta",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Provincia (Diurno)",
      dataIndex: "day_province",
      key: "day_province",
    },
    {
      title: "Cantón (Diurno)",
      dataIndex: "day_canton",
      key: "day_canton",
    },
    {
      title: "Provincia (Nocturno)",
      dataIndex: "night_province",
      key: "night_province",
    },
    {
      title: "Cantón (Nocturno)",
      dataIndex: "night_canton",
      key: "night_canton",
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <Title level={2}>
          Resultado Entre Base de Ventas AYASA VS Vehiculos con Dispositivo
        </Title>

        <Text type="secondary">
          Verifica la ubicación de los vehículos vendidos comparando con los
          registros de ubicación.
        </Text>

        <Divider />

        {/* Estadísticas */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: "24px" }}>
            <Col span={8}>
              <Statistic
                title="Total Base de Ventas AYASA"
                value={stats.total_sales}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Total Vehiculos con Dispositivo"
                value={stats.total_matches}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Porcentaje de Coincidencia"
                value={stats.match_percentage}
                suffix="%"
                precision={2}
              />
            </Col>
          </Row>
        )}

        {/* Controles */}
        <Space style={{ marginBottom: "24px" }}>
          <Tooltip title="Reinicia completamente la verificación. Borra todas las coincidencias existentes y las vuelve a crear. Afecta a TODOS los datos.">
            <Button
              type="primary"
              icon={<SyncOutlined />}
              loading={verifying}
              onClick={startVerification}
            >
              Iniciar Verificación <InfoCircleOutlined />
            </Button>
          </Tooltip>
          <Tooltip title="Solo actualiza las provincias en los registros existentes sin eliminar datos. Es un proceso más seguro y selectivo.">
            <Button
              type="default"
              icon={<SyncOutlined />}
              loading={updatingProvincias}
              onClick={updateProvincias}
            >
              Actualizar Provincias <InfoCircleOutlined />
            </Button>
          </Tooltip>
          <Input
            placeholder="Buscar por chasis, cantón o provincia"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: "350px" }}
            prefix={<SearchOutlined />}
          />
          <Button onClick={handleSearch} loading={loading}>
            Buscar
          </Button>
        </Space>

        {/* Mensajes de error */}
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: "24px" }}
          />
        )}

        {/* Tabla de coincidencias */}
        <Table
          columns={columns}
          dataSource={matches.data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            total: matches.total,
            onChange: (page) => fetchMatches(page, searchTerm),
          }}
        />
      </Card>
    </div>
  );
};

export default SalesLocationVerification;
